// Pure derivations for the GroundSpeedSetup calibration section.
// Kept free of React so they are unit-testable (groundSpeedUtils.test.ts).
import {
  GroundSpeedSetup,
  MagnetDetails,
  MagnetDetailsEntry,
  SPEED_POSITION,
  SpeedSensorEntry,
} from '../../types/CalibrationParameters';
import { RunMagnetCalc, SensorsActual, SensorsIntent } from '../../types/Run';

export type SpeedSource = 'front' | 'rear' | 'gps';

export const FRONT_POSITIONS: number[] = [SPEED_POSITION.FRONT, SPEED_POSITION.FRONT_ROLLER];
export const REAR_POSITIONS: number[] = [SPEED_POSITION.REAR, SPEED_POSITION.REAR_ROLLER];

export function sensorIsFront(position?: number): boolean {
  return FRONT_POSITIONS.includes(position ?? SPEED_POSITION.UNKNOWN);
}

export function sensorOffBike(position?: number): boolean {
  return position === SPEED_POSITION.FRONT_ROLLER || position === SPEED_POSITION.REAR_ROLLER;
}

// Whether a sensor is on the bike at the given wheel. An UNKNOWN position
// holds neither side — it must not read as rear just because it is not
// front, or a roster nobody has placed yet would hand the rear source to
// whichever sensor comes first.
export function sensorOnSide(position: number | undefined, front: boolean): boolean {
  const known = (position ?? SPEED_POSITION.UNKNOWN) !== SPEED_POSITION.UNKNOWN;
  return known && !sensorOffBike(position) && sensorIsFront(position) === front;
}

// Short human label for a sensor's wheel position — null for UNKNOWN so
// callers can omit the suffix rather than print 'Unknown'.
export function sensorPositionLabel(position?: number): string | null {
  switch (position) {
    case SPEED_POSITION.FRONT: return 'Front';
    case SPEED_POSITION.REAR: return 'Rear';
    case SPEED_POSITION.FRONT_ROLLER: return 'Front roller';
    case SPEED_POSITION.REAR_ROLLER: return 'Rear roller';
    default: return null;
  }
}

// How a sensor is named wherever a plot has to say WHICH sensor it means:
// its wheel position where the run records one, and always its ANT device
// number — 'Front (ID 46145)', or 'ID 46145' on its own. Positions only
// exist for speed and tyre-pressure sensors, so a power meter or a wind
// probe reads as the id alone.
export function sensorIdentity(id: number, position?: number): string {
  const pos = sensorPositionLabel(position);
  return pos ? `${pos} (ID ${id})` : `ID ${id}`;
}

export function positionFor(front: boolean, offBike: boolean): number {
  if (front) {
    return offBike ? SPEED_POSITION.FRONT_ROLLER : SPEED_POSITION.FRONT;
  }
  return offBike ? SPEED_POSITION.REAR_ROLLER : SPEED_POSITION.REAR;
}

export function primaryOf(setup: GroundSpeedSetup | null | undefined): SpeedSensorEntry | undefined {
  if (!setup) return undefined;
  const sensors = setup.sensors || [];
  const byId = sensors.find((s) => s.id_ === setup.primary_id);
  if (byId) return byId;
  if (sensors.length === 1) return sensors[0];
  // Last resort: the first ON-BIKE sensor on the effective side (the
  // stored source_side, front by default). Rollers never fall out.
  const side = setup.source_side === SPEED_POSITION.REAR ? SPEED_POSITION.REAR : SPEED_POSITION.FRONT;
  return sensors.find((s) => s.position === side);
}

// The radio row's current selection: GPS wins, else the stored
// source_side, else the primary sensor's wheel position (front by
// convention when nothing says otherwise).
export function speedSource(setup: GroundSpeedSetup | null | undefined): SpeedSource {
  if (!setup) return 'front';
  if (setup.gps_speed) return 'gps';
  if (setup.source_side === SPEED_POSITION.REAR) return 'rear';
  if (setup.source_side === SPEED_POSITION.FRONT) return 'front';
  const primary = primaryOf(setup);
  if (primary && REAR_POSITIONS.includes(primary.position ?? SPEED_POSITION.UNKNOWN)) return 'rear';
  return 'front';
}

// Selecting a source ALWAYS stores the side (`source_side`) — the choice
// is storable with an empty roster, which is how a new session carries
// front/rear before any sensor ids exist. GPS keeps the stored side for
// round-trip. With a sole sensor the source additionally decides its wheel
// position outright and it becomes primary; with several, primary_id
// repoints at the on-bike sensor holding that position (null when none
// does — shown as '…').
export function setSpeedSource(setup: GroundSpeedSetup, source: SpeedSource): GroundSpeedSetup {
  const sensors = setup.sensors || [];
  if (source === 'gps') {
    return { ...setup, gps_speed: true };
  }
  const srcFront = source === 'front';
  const sourceSide = srcFront ? SPEED_POSITION.FRONT : SPEED_POSITION.REAR;
  if (sensors.length === 0) {
    return {
      ...setup, gps_speed: false, source_side: sourceSide, primary_id: null,
    };
  }
  if (sensors.length === 1) {
    const sole = sensors[0];
    return {
      ...setup,
      gps_speed: false,
      source_side: sourceSide,
      primary_id: sole.id_,
      sensors: [{ ...sole, position: positionFor(srcFront, false) }],
    };
  }
  const onSide = (s: SpeedSensorEntry) => sensorOnSide(s.position, srcFront);
  const current = sensors.find((s) => s.id_ === setup.primary_id);
  const match = current && onSide(current) ? current : sensors.find(onSide);
  return {
    ...setup, gps_speed: false, source_side: sourceSide, primary_id: match ? match.id_ : null,
  };
}

// Section invariant for wheel sources: the primary sensor is an on-bike
// sensor on the speed-source side, and at least one sensor holds that side.
// Several sensors MAY share a side — the UI disambiguates with 'Set primary'.
// A sole sensor is pinned to the source side (its location radios are
// disabled in the UI). Off-bike (roller) sensors never hold the source side
// and never become primary. When the current holder leaves the source side
// and nobody else is on it, the first other on-bike sensor swaps over.
function reconcileWithSource(
  setup: GroundSpeedSetup, source: SpeedSource, changedId?: number,
): GroundSpeedSetup {
  const sensors = setup.sensors || [];
  if (source === 'gps' || sensors.length === 0) return setup;
  const srcFront = source === 'front';
  if (sensors.length === 1) {
    const sole = sensors[0];
    return {
      ...setup,
      primary_id: sole.id_,
      sensors: [{ ...sole, position: positionFor(srcFront, false) }],
    };
  }
  const onSide = (s: SpeedSensorEntry) => sensorOnSide(s.position, srcFront);
  const currentPrimary = sensors.find((s) => s.id_ === setup.primary_id);
  if (currentPrimary && onSide(currentPrimary)) return setup;
  const changed = sensors.find((s) => s.id_ === changedId);
  if (changed && onSide(changed)) return { ...setup, primary_id: changed.id_ };
  const holder = sensors.find((s) => s.id_ !== changedId && onSide(s));
  if (holder) return { ...setup, primary_id: holder.id_ };
  const candidate = sensors.find((s) => s.id_ !== changedId && !sensorOffBike(s.position));
  if (candidate) {
    const next = sensors.map((s) => (
      s.id_ === candidate.id_ ? { ...s, position: positionFor(srcFront, false) } : s
    ));
    return { ...setup, sensors: next, primary_id: candidate.id_ };
  }
  return { ...setup, primary_id: null };
}

// 'Set primary' among several sensors sharing the primary's side.
export function setPrimarySensor(setup: GroundSpeedSetup, id: number): GroundSpeedSetup {
  if (!(setup.sensors || []).some((s) => s.id_ === id)) return setup;
  return { ...setup, primary_id: id };
}

// The sensor the section treats as primary, read-side. primary_id can be
// null or stale (legacy-synthesised objects, device-authored runs), so the
// rule is applied on read too: a sole sensor is always primary; the sole
// on-bike sensor on the speed-source side is primary whatever primary_id
// says; several candidates defer to primary_id or stay unresolved (null).
export function effectivePrimaryId(
  setup: GroundSpeedSetup, source: SpeedSource,
): number | null {
  const sensors = setup.sensors || [];
  if (sensors.length === 0) return null;
  if (sensors.length === 1) return sensors[0].id_;
  if (source === 'gps') return setup.primary_id ?? null;
  const srcFront = source === 'front';
  const onSide = sensors.filter((s) => sensorOnSide(s.position, srcFront));
  if (onSide.some((s) => s.id_ === setup.primary_id)) return setup.primary_id ?? null;
  if (onSide.length === 1) return onSide[0].id_;
  return null;
}

export function updateSensor(
  setup: GroundSpeedSetup, id: number, patch: Partial<SpeedSensorEntry>,
): GroundSpeedSetup {
  const sensors = (setup.sensors || []).map((s) => (s.id_ === id ? { ...s, ...patch } : s));
  return { ...setup, sensors };
}

export function setSensorLocation(
  setup: GroundSpeedSetup, id: number, front: boolean, source: SpeedSource,
): GroundSpeedSetup {
  const sensor = (setup.sensors || []).find((s) => s.id_ === id);
  const offBike = sensorOffBike(sensor?.position);
  return reconcileWithSource(updateSensor(setup, id, { position: positionFor(front, offBike) }), source, id);
}

export function setSensorOffBike(
  setup: GroundSpeedSetup, id: number, offBike: boolean, source: SpeedSource,
): GroundSpeedSetup {
  const sensor = (setup.sensors || []).find((s) => s.id_ === id);
  const front = sensor ? sensorIsFront(sensor.position) : true;
  return reconcileWithSource(updateSensor(setup, id, { position: positionFor(front, offBike) }), source, id);
}

export function removeSensorSpacing(setup: GroundSpeedSetup, id: number): GroundSpeedSetup {
  return updateSensor(setup, id, { magnet_spacing: null });
}

export interface LegacySpeedFields {
  gps_speed?: boolean | null;
  speed_sensor_pos?: number | null;
  wheel_magnet_count?: number | null;
  magnet_details?: MagnetDetails | null;
}

// Read-fallback for configs whose ground_speed_setup column is NULL —
// mirrors the server's GroundSpeedSetup.from_legacy: sensors come from the
// magnet_details keys, speed_sensor_pos becomes source_side whenever it
// was set (config-wide), the single position column only describes a sole
// sensor, and alt_spd is not representable (primary_id stays null).
export function fromLegacy(legacy: LegacySpeedFields): GroundSpeedSetup {
  const details = legacy.magnet_details || {};
  const keys = Object.keys(details).sort((a, b) => Number(a) - Number(b));
  const single = keys.length === 1;
  const defaultCount = Math.max(1, Number(legacy.wheel_magnet_count ?? 1));
  const sensors: SpeedSensorEntry[] = keys.map((key) => ({
    id_: Number(key),
    position: single
      ? (legacy.speed_sensor_pos === 1 ? SPEED_POSITION.FRONT : SPEED_POSITION.REAR)
      : SPEED_POSITION.UNKNOWN,
    magnet_count: Number(details[key]?.magnet_count ?? defaultCount),
    magnet_spacing: details[key] ?? null,
  }));
  const sourceSide = legacy.speed_sensor_pos == null
    ? null
    : (legacy.speed_sensor_pos === 1 ? SPEED_POSITION.FRONT : SPEED_POSITION.REAR);
  return {
    gps_speed: Boolean(legacy.gps_speed), primary_id: null, source_side: sourceSide, sensors,
  };
}

// The effective section value: the stored object, else the legacy view.
// A side-only object (a new session's empty-roster front/rear choice) IS
// a stored value and must not fall back to the legacy synthesis.
export function effectiveSetup(
  value: GroundSpeedSetup | null | undefined, legacy: LegacySpeedFields,
): GroundSpeedSetup {
  if (value && (value.sensors?.length || value.gps_speed
    || value.primary_id != null || value.source_side != null)) {
    return value;
  }
  return fromLegacy(legacy);
}

export function changeSensorId(setup: GroundSpeedSetup, oldId: number, newId: number): GroundSpeedSetup {
  if (oldId === newId || (setup.sensors || []).some((s) => s.id_ === newId)) return setup;
  const sensors = (setup.sensors || []).map((s) => (s.id_ === oldId ? { ...s, id_: newId } : s));
  const primaryId = setup.primary_id === oldId ? newId : setup.primary_id;
  return { ...setup, sensors, primary_id: primaryId };
}

// ---- Intent/actual split records (run side) ----

// ANT hashId arithmetic: device_type * 10**6 + device_number.
const DIV_NUM = 1_000_000;

/**
 * Device number out of an ANT hashId. The device number alone is what the
 * rest of the UI shows a rider, so every surface that renders a sensor id
 * goes through here rather than restating the arithmetic.
 */
export function antDeviceNumber(hashId: number): number {
  return hashId % DIV_NUM;
}

/** SensorFunctionsEnum member names, as `sensors_*.function` stores them. */
export const SENSOR_FUNCTION = {
  POWER: 'PowerMeter',
  SPEED: 'SpeedSensor',
  WIND: 'WindSensor',
} as const;

// The run fields the wheel-setup synthesis reads. Matches the Run type;
// declared structurally so callers can pass any run-shaped object.
// (`ground_speed_setup` is no longer an API column — the structural
// fallback covers historical payload shapes and tests only.)
export interface RunWheelRecordSource {
  ground_speed_setup?: GroundSpeedSetup | null;
  sensors_intent?: SensorsIntent | null;
  sensors_actual?: SensorsActual | null;
  magnet_calc?: RunMagnetCalc | null;
}

// Synthesise the run's wheel setup from the split records — the client
// mirror of the server's speed_cadence_ops._setup_from_records. The
// device-observed `sensors_actual` is the binding authority and is
// REQUIRED (a backfilled claim alone must not shadow a stored setup);
// `sensors_intent` contributes the claims (position, primary, count,
// gps); the id-keyed `magnet_calc` ledger contributes this run's fits
// (legacy bare/i{n} shapes deliberately don't match ids and are ignored).
export function setupFromRunRecords(
  run: RunWheelRecordSource | null | undefined,
): GroundSpeedSetup | null {
  const actualSensors = run?.sensors_actual?.sensors;
  if (!actualSensors) return null;
  const actualSpeed = actualSensors.filter(
    (s) => (s.source ?? 'sensor') === 'sensor'
      && s.function === SENSOR_FUNCTION.SPEED && s.hash_id != null,
  );
  const intentSpeed = (run?.sensors_intent?.sensors ?? [])
    .filter((s) => s.function === SENSOR_FUNCTION.SPEED);
  const instanceByHash = new Map<number, number | null>(
    actualSpeed.map((s) => [s.hash_id as number, s.run_instance ?? null]),
  );
  const hashes = [...new Set([
    ...instanceByHash.keys(), ...intentSpeed.map((s) => s.hash_id),
  ])].sort((a, b) => a - b);
  const mc = run?.magnet_calc;
  const fits: Record<string, MagnetDetailsEntry> = (
    mc && typeof mc === 'object' && !('magnet_count' in mc)
  ) ? mc as Record<string, MagnetDetailsEntry> : {};

  const sensors: SpeedSensorEntry[] = hashes.map((hash) => {
    const claim = intentSpeed.find((s) => s.hash_id === hash);
    const id = antDeviceNumber(hash);
    return {
      id_: id,
      position: claim?.position ?? SPEED_POSITION.UNKNOWN,
      run_instance: instanceByHash.get(hash) ?? undefined,
      magnet_count: claim?.magnet_count ?? undefined,
      magnet_spacing: fits[String(id)] ?? undefined,
    };
  });
  const gpsSpeed = Boolean(run?.sensors_intent?.gps_speed);
  if (sensors.length === 0 && !gpsSpeed) return null;
  const primaryClaim = intentSpeed.find((s) => s.primary);
  return {
    gps_speed: gpsSpeed,
    primary_id: primaryClaim ? antDeviceNumber(primaryClaim.hash_id) : null,
    sensors,
  };
}

// The run's wheel setup for display: split records first, stored
// ground_speed_setup for runs that predate them.
export function runWheelSetup(
  run: RunWheelRecordSource | null | undefined,
): GroundSpeedSetup | null | undefined {
  return setupFromRunRecords(run) ?? run?.ground_speed_setup;
}

// The run fields that decide WHICH sensor fed the primary wheel series.
export interface RunWheelLabelSource extends RunWheelRecordSource {
  solve?: { selections?: { speed?: { ant_device_number?: number | null } | null } | null } | null;
}

// Which physical sensor each of the pipeline's two wheel series came from.
// The solve's recorded selection (`speed.ant_device_number`) is
// authoritative for the primary — `primary_id` can lose a binding and fall
// back — with `primaryOf` covering runs processed before the selection
// carried the id. With exactly two sensors the second series is the other
// one; beyond that it stays unresolved rather than guessed.
export function wheelSensorPair(
  run: RunWheelLabelSource | null | undefined,
): { primary?: SpeedSensorEntry; alt?: SpeedSensorEntry } {
  const setup = runWheelSetup(run);
  const sensors = setup?.sensors ?? [];
  if (sensors.length === 0) return {};
  const selectedId = run?.solve?.selections?.speed?.ant_device_number;
  const primary = sensors.find((s) => s.id_ === selectedId) ?? primaryOf(setup);
  if (!primary) return {};
  const others = sensors.filter((s) => s.id_ !== primary.id_);
  return { primary, alt: others.length === 1 ? others[0] : undefined };
}

// ---- Promoted spacing overlay (config side) ----

// The spacing to DISPLAY for one sensor: the magnet_details column entry
// (where autocal promotions land under the intent/actual split) wins;
// spacing embedded in the roster object by the branch era fills gaps.
// `promoted` tells the UI the value is autocal-owned (no Clear).
export function effectiveSensorSpacing(
  sensor: SpeedSensorEntry, magnetDetails?: MagnetDetails | null,
): { spacing: MagnetDetailsEntry; promoted: boolean } | null {
  const column = magnetDetails?.[String(sensor.id_)];
  if (column) return { spacing: column, promoted: true };
  if (sensor.magnet_spacing) return { spacing: sensor.magnet_spacing, promoted: false };
  return null;
}

// ---- Sensors the runs recorded (config side) ----

// The run fields that name a speed sensor: the intent/actual records, and
// the pipeline's own selection for runs that carry neither.
export interface RunRecordedSpeedSource extends RunWheelRecordSource {
  solve?: {
    selections?: {
      speed?: {
        ant_device_number?: number | null;
        alternate_ant_device_number?: number | null;
      } | null;
    } | null;
  } | null;
}

// Every speed sensor the given runs recorded, one entry per device number,
// sorted by id. A position or magnet count the device claimed is carried
// over (the pipeline reads the run's claim before the config's anyway);
// otherwise the position stays UNKNOWN for the user to set.
export function recordedSpeedSensors(runs: RunRecordedSpeedSource[]): SpeedSensorEntry[] {
  const byId = new Map<number, SpeedSensorEntry>();
  const note = (id: number | null | undefined, position?: number, magnetCount?: number | null) => {
    if (id == null || id <= 0) return;
    const entry = byId.get(id) ?? { id_: id, position: SPEED_POSITION.UNKNOWN };
    if (entry.position === SPEED_POSITION.UNKNOWN && position != null) entry.position = position;
    if (entry.magnet_count == null && magnetCount) entry.magnet_count = magnetCount;
    byId.set(id, entry);
  };
  runs.forEach((run) => {
    (run.sensors_intent?.sensors ?? [])
      .filter((s) => s.function === SENSOR_FUNCTION.SPEED)
      .forEach((s) => note(antDeviceNumber(s.hash_id), s.position, s.magnet_count));
    (run.sensors_actual?.sensors ?? [])
      .filter((s) => (s.source ?? 'sensor') === 'sensor' && s.function === SENSOR_FUNCTION.SPEED)
      .forEach((s) => note(s.hash_id == null ? null : antDeviceNumber(s.hash_id)));
    const speed = run.solve?.selections?.speed;
    note(speed?.ant_device_number);
    note(speed?.alternate_ant_device_number);
  });
  return [...byId.values()].sort((a, b) => a.id_ - b.id_);
}

// The config-shaped fields needed to tell which configs take their speed
// setup from a given one.
export interface SpeedSetupConfigNode {
  id: string;
  baseline_id?: string | null;
  parameters_inherited?: Record<string, unknown> | null;
  runs?: RunRecordedSpeedSource[] | null;
}

// The runs whose speed setup comes from config `configId`: its own runs,
// plus the runs of every config that inherits `ground_speed_setup` from it
// through the baseline chain. A config holding its own value stops the walk.
export function runsUsingSpeedSetup(
  configs: SpeedSetupConfigNode[], configId: string,
): RunRecordedSpeedSource[] {
  const byId = new Map(configs.map((c) => [c.id, c]));
  const usesSetupOf = (config: SpeedSetupConfigNode): boolean => {
    const seen = new Set<string>();
    let current: SpeedSetupConfigNode | undefined = config;
    while (current && !seen.has(current.id)) {
      if (current.id === configId) return true;
      seen.add(current.id);
      if (!current.parameters_inherited || !('ground_speed_setup' in current.parameters_inherited)) return false;
      current = current.baseline_id ? byId.get(current.baseline_id) : undefined;
    }
    return false;
  };
  return configs.filter(usesSetupOf).flatMap((c) => c.runs ?? []);
}

// The setup with every recorded sensor the roster lacks appended. The
// roster is what lets a sensor hold a position; without it the speed
// source side has nothing to match and processing falls back to the
// device's primary. Returns the same object when nothing is missing.
export function withRecordedSensors(
  setup: GroundSpeedSetup, recorded: SpeedSensorEntry[],
): GroundSpeedSetup {
  const have = new Set((setup.sensors || []).map((s) => s.id_));
  const missing = recorded.filter((s) => !have.has(s.id_));
  if (missing.length === 0) return setup;
  return { ...setup, sensors: [...(setup.sensors || []), ...missing.map((s) => ({ ...s }))] };
}

// Why the selected wheel source does not pick a sensor, or null when it
// does: no on-bike sensor holds that side ('no_sensor'), or several do and
// none is marked primary ('several'). GPS and an empty roster never warn.
export function sourceSideProblem(
  setup: GroundSpeedSetup, source: SpeedSource,
): 'no_sensor' | 'several' | null {
  const sensors = setup.sensors || [];
  if (source === 'gps' || sensors.length <= 1) return null;
  const srcFront = source === 'front';
  const onSide = sensors.filter((s) => sensorOnSide(s.position, srcFront));
  if (onSide.length === 0) return 'no_sensor';
  if (onSide.length > 1 && !onSide.some((s) => s.id_ === setup.primary_id)) return 'several';
  return null;
}
